import { defineStore } from 'pinia'
import Qs from 'qs'

/**
 * Location store — shared state for all location dashboard pages.
 *
 * Populated by `OrganizationsLocations` styxie via `setLocationData(props)`.
 * Used by: dashboard, edit, media, reviews, local posts, change log pages.
 *
 * @property {Object|null} location - Core location record (id, name, etc.)
 * @property {Object|null} gmbLocation - GMB-specific data (title, trackedGoogleUpdates, googleUpdatedData)
 * @property {Object} capabilities - Feature capabilities for this location
 * @property {Object|null} editData - Edit form data (includes editData.gmbLocation for editable fields)
 * @property {Array} tagOptions - Available tag options for the location
 */
export const useLocationStore = defineStore('location', {
  state: () => ({
    location: null,
    gmbLocation: null,
    capabilities: {},
    callTracking: null,
    editData: null,
    tagOptions: [],

    loading: false,
    error: null
  }),
  getters: {
    hasGmbLocation: (state) => state.gmbLocation != null,
    locationName: (state) => {
      if (state.gmbLocation?.title) return state.gmbLocation.title
      if (state.location?.name) return state.location.name
      return ''
    },
    hadGoogleUpdate: (state) => (key) => state.gmbLocation?.trackedGoogleUpdates?.includes(key) || false,
    googleUpdatedData: (state) => state.gmbLocation?.googleUpdatedData?.location || null,
    locationFilteredLink: (state) => (href, customParams = {}) => {
      if (!state.location?.id) return href

      const params = {
        locationId: state.location.id,
        ...customParams
      }

      return `${href}?${Qs.stringify(params)}`
    }
  },
  actions: {
    setLocationData(data) {
      this.location = data.location || null
      this.gmbLocation = data.gmbLocation || null
      this.capabilities = data.capabilities || {}
      this.callTracking = data.callTracking || null
      this.editData = data.editData || null
      this.tagOptions = data.tagOptions || []
    },
    setLoading(loading) {
      this.loading = loading
    },
    setError(error) {
      this.error = error
    },
    updateGmbLocationData(fields) {
      if (!this.gmbLocation) return
      Object.assign(this.gmbLocation, fields)
    },
    updateLocationData(fields) {
      if (!this.location) return
      Object.assign(this.location, fields)
    },
    updateEditGmbLocationData(fields) {
      if (!this.editData?.gmbLocation) return
      Object.assign(this.editData.gmbLocation, fields)
    },
    updateEditCapabilities(fields) {
      if (!this.editData?.capabilities) return
      Object.assign(this.editData.capabilities, fields)
    }
  }
})
